import { BoldButton } from '@components/core-ui/form-elements/buttons/Button/variations/BoldButton';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { Input } from '@components/core-ui/form-elements/Input';
import TrashBin from '@components/icons/TrashBin.svg';
import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import { PasswordField } from '@components/core-ui/form-elements/PasswordField';
import { uploadAvatar } from '@helpers/uploadS3Image';
import { PASSWORD_REQUIREMENTS_HINT } from '@helpers/validatePassword';
import { usePasswordFieldErrors } from 'app/hooks/usePasswordFieldErrors';
import { useAuthContext } from '@providers/AuthProvider';
import { useModalContext } from '@providers/ModalProvider';
import { useAPI } from '@providers/useAPI';
import { UpdateAccountBodyDto, UserService } from 'app/API';
import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';
import { useMutation } from '@tanstack/react-query';
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { EntityIconProfile } from '@components/core-ui/EntityIcon/variations/EntityIconProfile';
import { FilePicker } from '@components/FilePicker';
import { AvatarBlock } from './styles/AvatarBlock';
import { Description } from './styles/Description';
import { InputBlockWrapper } from './styles/InputBlockWrapper';
import { InputWrapper } from './styles/InputWrapper';
import { LeftSide } from './styles/LeftSide';
import { IconButtonDelete } from '@components/core-ui/form-elements/buttons/IconButton/variations/IconButtonDelete';
import { RightSide } from './styles/RightSide';
import { RightSideColumns } from './styles/RightSideColumns';
import { Wrapper } from './styles/Wrapper';
import { getUpdateAccountErrorMessage } from './helpers/getUpdateAccountErrorMessage';

export const UserDetails = () => {
  const { call } = useAPI();
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  const { me, updateMe } = useAuthContext();
  const { openModal, closeModal } = useModalContext();

  const [form, setForm] = useState({
    name: me.name,
    lastname: me.lastname,
    email: me.email,
    phone: me.phone,
    password: '',
    repeatPassword: '',
  });
  const {
    passwordRef,
    confirmationRef,
    passwordError,
    confirmationError,
    onPasswordBlur,
    onConfirmationBlur,
    validatePasswordFields,
  } = usePasswordFieldErrors(form.password, form.repeatPassword);

  const removeAvatarMutation = useMutation({
    mutationFn: () => call(() => UserService.deleteAvatar()),
    onSuccess: () => updateMe(),
    onError: () => toast.error('Could not remove the picture.'),
  });

  const updateAccountMutation = useMutation({
    mutationFn: (requestBody: UpdateAccountBodyDto) => call(() => UserService.updateAccount({ requestBody })),
    onSuccess: async (result) => {
      if (result.error) {
        toast.error(result.error);
        return;
      }
      await updateMe();
      toast.success('Successfully updated');
    },
    onError: (updateError) => toast.error(getUpdateAccountErrorMessage(updateError)),
  });
  const isSubmitting = updateAccountMutation.isPending;

  const submitAvatar = (file: File) => {
    openModal(ModalNamesEnum.ImageUploadModal, {
      file,
      onSuccess: async (avatar: string) => {
        try {
          await uploadAvatar(avatar);
          await updateMe();
        } catch (uploadError) {
          toast.error(uploadError instanceof Error ? uploadError.message : 'Could not upload the picture.');
        }
        closeModal();
      },
    });
  };

  const renderAvatar = () => {
    return (
      <AvatarBlock>
        <EntityIconProfile name={`${me.name} ${me.lastname}`} src={me.avatar ?? undefined} />
        <FilePicker accept="image/jpeg,image/png" onFile={submitAvatar}>
          <OutlinedButton className="w-32" type="button">
            Upload Picture
          </OutlinedButton>
        </FilePicker>
        {me.avatar ? (
          <IconButtonDelete
            title="Remove"
            disabled={removeAvatarMutation.isPending}
            onClick={() => removeAvatarMutation.mutate()}
          >
            <TrashBin />
          </IconButtonDelete>
        ) : null}
      </AvatarBlock>
    );
  };

  const submit = () => {
    if (!validatePasswordFields()) {
      return;
    }

    const formFields: UpdateAccountBodyDto = {
      name: form.name,
      lastname: form.lastname,
    };

    if (form.password) {
      formFields.password = form.password;
    }

    updateAccountMutation.mutate(formFields);
  };

  return (
    <>
      <Wrapper>
        {isSubmitting && <AbsoluteSpinner overlay={true} />}
        <LeftSide>
          <h3>General</h3>
          <p>Update your account details and password.</p>
        </LeftSide>

        <RightSide>
          {renderAvatar()}
          <RightSideColumns>
            <InputBlockWrapper>
              <InputWrapper>
                <label htmlFor="name">First name</label>
                <Input
                  className="form-input w-full"
                  disabled={isSubmitting}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setForm({
                      ...form,
                      name: e.target.value,
                    })
                  }
                  name="name"
                  value={form.name}
                  label="First name"
                />
              </InputWrapper>
              <InputWrapper>
                <label htmlFor="email">Email address</label>
                <Input
                  className="form-input w-full"
                  disabled={true}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setForm({
                      ...form,
                      email: e.target.value,
                    })
                  }
                  name="email"
                  value={form.email}
                  label="Email address"
                />
              </InputWrapper>
            </InputBlockWrapper>
            <InputBlockWrapper>
              <InputWrapper>
                <label htmlFor="lastName">Last name</label>
                <Input
                  className="form-input w-full"
                  disabled={isSubmitting}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setForm({
                      ...form,
                      lastname: e.target.value,
                    })
                  }
                  name="lastName"
                  value={form.lastname}
                  label="Last name"
                />
              </InputWrapper>
              <InputWrapper>
                <label htmlFor="phone">Phone</label>
                <Input
                  className="form-input w-full"
                  disabled={true}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setForm({
                      ...form,
                      phone: e.target.value,
                    })
                  }
                  name="phone"
                  value={form.phone}
                  label="Phone"
                />
              </InputWrapper>
            </InputBlockWrapper>
          </RightSideColumns>

          <Description>
            For assistance in changing your email or phone number, please contact{' '}
            <a
              href="https://support.voicescript.ai/portal/en/home"
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              support
            </a>
            .
          </Description>

          <RightSideColumns>
            <InputBlockWrapper>
              <InputWrapper>
                <label htmlFor="newPassword">New password</label>
                <PasswordField
                  ref={passwordRef}
                  name="password"
                  label="New password"
                  value={form.password}
                  onChange={(password) => setForm({ ...form, password })}
                  onBlur={onPasswordBlur}
                  isVisible={isPasswordVisible}
                  onVisibilityToggle={() => setIsPasswordVisible(!isPasswordVisible)}
                  hint={PASSWORD_REQUIREMENTS_HINT}
                  error={passwordError}
                  autoComplete="new-password"
                  required={true}
                  disabled={isSubmitting}
                />
              </InputWrapper>
            </InputBlockWrapper>
            <InputBlockWrapper>
              <InputWrapper>
                <label htmlFor="repeatPassword">Repeat password</label>
                <PasswordField
                  ref={confirmationRef}
                  name="repeatPassword"
                  label="Repeat password"
                  value={form.repeatPassword}
                  onChange={(repeatPassword) => setForm({ ...form, repeatPassword })}
                  onBlur={onConfirmationBlur}
                  isVisible={isPasswordVisible}
                  onVisibilityToggle={() => setIsPasswordVisible(!isPasswordVisible)}
                  error={confirmationError}
                  autoComplete="new-password"
                  required={true}
                  disabled={isSubmitting}
                />
              </InputWrapper>
            </InputBlockWrapper>
          </RightSideColumns>
        </RightSide>
      </Wrapper>

      <div className="flex justify-end">
        <BoldButton className="my-2 w-[140px]" disabled={isSubmitting} onClick={submit}>
          Save
        </BoldButton>
      </div>
    </>
  );
};
